import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  BookOpen, Search, Eye, ThumbsUp, ThumbsDown, Clock, Tag, User, 
  Share2, ArrowLeft, Edit3, Archive, Plus, CheckCircle2, Shield,
  Laptop, Mail, Wifi, KeyRound, Box, Trash2, Ticket as TicketIcon,
  Sparkles, History, BarChart3, MessageSquare, AlertTriangle, ExternalLink,
  ChevronRight, RefreshCw, Send, Check, Folder, FolderOpen, Layers
} from 'lucide-react';
import toast from 'react-hot-toast';
import apiClient from '../../../lib/api';
import { cn } from '../../../lib/utils';
import { useAuthStore } from '../../../store/authStore';

const getCategoryIcon = (name?: string) => {
  const n = (name || '').toLowerCase();
  if (n.includes('hardware') || n.includes('device')) return Laptop;
  if (n.includes('network') || n.includes('wifi') || n.includes('connect')) return Wifi;
  if (n.includes('security') || n.includes('access') || n.includes('sso') || n.includes('auth')) return Shield;
  if (n.includes('software') || n.includes('app')) return Box;
  if (n.includes('email') || n.includes('mail')) return Mail;
  return Folder;
};

export interface KBCategory {
  id: number;
  name: string;
  slug: string;
  description?: string;
  icon?: string;
  article_count?: number;
}

export interface KBArticle {
  id: number;
  category_id: number;
  category_name?: string;
  title: string;
  slug: string;
  summary: string;
  content: string;
  status: 'draft' | 'published' | 'archived';
  visibility: 'public' | 'internal' | 'restricted';
  tags: string[];
  author_id?: string | null;
  author_name?: string | null;
  views_count: number;
  helpful_count: number;
  not_helpful_count: number;
  read_time_minutes: number;
  published_at?: string | null;
  created_at: string;
  updated_at: string;
}

export interface KBAnalytics {
  topViewed: Array<{ id: number; title: string; views: number; helpful: number }>;
  unhelpful: Array<{ id: number; title: string; not_helpful: number; helpful: number }>;
  noResultSearches: Array<{ query: string; count: number }>;
  aiDeflections: {
    totalChats: number;
    deflectedWithoutTicket: number;
    deflectionRatePct: number;
  };
}

export function KnowledgeBase() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuthStore();

  // Mode: list | reader | editor | analytics
  const [activeTab, setActiveTab] = useState<'articles' | 'analytics'>(
    (searchParams.get('tab') as any) || 'articles'
  );
  const [viewMode, setViewMode] = useState<'browse' | 'read' | 'edit'>('browse');

  // Data from DB
  const [categories, setCategories] = useState<KBCategory[]>([]);
  const [articles, setArticles] = useState<KBArticle[]>([]);
  const [selectedArticle, setSelectedArticle] = useState<KBArticle | null>(null);
  const [analytics, setAnalytics] = useState<KBAnalytics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [kbFeatureEnabled, setKbFeatureEnabled] = useState(true);

  // Filters
  const selectedCategoryId = searchParams.get('category') ? parseInt(searchParams.get('category')!, 10) : null;
  const searchQuery = searchParams.get('q') || '';
  const statusFilter = searchParams.get('status') || 'published';

  // Editor form state
  const [editorId, setEditorId] = useState<number | null>(null);
  const [editorTitle, setEditorTitle] = useState('');
  const [editorCategoryId, setEditorCategoryId] = useState<number>(1);
  const [editorSummary, setEditorSummary] = useState('');
  const [editorContent, setEditorContent] = useState('');
  const [editorStatus, setEditorStatus] = useState<'draft' | 'published' | 'archived'>('published');
  const [editorVisibility, setEditorVisibility] = useState<'public' | 'internal'>('public');
  const [editorTags, setEditorTags] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Versions history
  const [versions, setVersions] = useState<any[]>([]);
  const [showVersions, setShowVersions] = useState(false);

  // Load KB feature flag from settings
  const checkFeatureFlag = useCallback(async () => {
    try {
      const res = await apiClient.get('/support/settings/general/kb_enabled');
      if (res.data?.data !== undefined) {
        setKbFeatureEnabled(Boolean(res.data.data));
      }
    } catch {
      // Default enabled if setting not yet initialized
      setKbFeatureEnabled(true);
    }
  }, []);

  // Fetch real categories from DB
  const fetchCategories = useCallback(async () => {
    try {
      const res = await apiClient.get('/support/kb/categories');
      setCategories(res.data?.data || []);
    } catch (e) {
      console.warn('Failed to fetch KB categories:', e);
    }
  }, []);

  // Fetch real articles from DB
  const fetchArticles = useCallback(async () => {
    setIsLoading(true);
    try {
      const params: Record<string, any> = {};
      if (selectedCategoryId) params.categoryId = selectedCategoryId;
      if (searchQuery.trim()) params.search = searchQuery.trim();
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;

      const res = await apiClient.get('/support/kb/articles', { params });
      const list = res.data?.data || [];
      setArticles(list);

      // If URL has articleId, open it
      const urlArticleId = searchParams.get('articleId');
      if (urlArticleId) {
        const found = list.find((a: KBArticle) => a.id === parseInt(urlArticleId, 10));
        if (found) {
          setSelectedArticle(found);
          setViewMode('read');
        }
      }
    } catch (e) {
      console.warn('Failed to fetch KB articles:', e);
      setArticles([]);
    } finally {
      setIsLoading(false);
    }
  }, [selectedCategoryId, searchQuery, statusFilter, searchParams]);

  // Fetch real DB analytics
  const fetchAnalytics = useCallback(async () => {
    try {
      const res = await apiClient.get('/support/kb/analytics');
      setAnalytics(res.data?.data || null);
    } catch (e) {
      console.warn('Failed to load KB analytics:', e);
    }
  }, []);

  useEffect(() => {
    checkFeatureFlag();
    fetchCategories();
  }, [checkFeatureFlag, fetchCategories]);

  useEffect(() => {
    if (activeTab === 'articles') {
      fetchArticles();
    } else if (activeTab === 'analytics') {
      fetchAnalytics();
    }
  }, [activeTab, fetchArticles, fetchAnalytics]);

  // Select article & record DB view
  const handleOpenArticle = async (article: KBArticle) => {
    setSelectedArticle(article);
    setViewMode('read');
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('articleId', String(article.id));
      return next;
    });

    try {
      // Record real DB view
      await apiClient.get(`/support/kb/${article.id}`);
    } catch {
      // ignore
    }
  };

  // Submit helpful/unhelpful feedback to DB
  const handleFeedback = async (isHelpful: boolean) => {
    if (!selectedArticle) return;
    try {
      await apiClient.post(`/support/kb/${selectedArticle.id}/feedback`, {
        isHelpful,
      });
      toast.success(isHelpful ? 'Marked as helpful' : 'Feedback recorded');
      // Update local state
      setSelectedArticle((prev) => prev ? {
        ...prev,
        helpful_count: isHelpful ? prev.helpful_count + 1 : prev.helpful_count,
        not_helpful_count: !isHelpful ? prev.not_helpful_count + 1 : prev.not_helpful_count,
      } : null);
    } catch {
      toast.error('Failed to submit feedback');
    }
  };

  // Open editor for new or existing article
  const handleStartEdit = (article?: KBArticle) => {
    if (article) {
      setEditorId(article.id);
      setEditorTitle(article.title);
      setEditorCategoryId(article.category_id);
      setEditorSummary(article.summary || '');
      setEditorContent(article.content || '');
      setEditorStatus(article.status);
      setEditorVisibility(article.visibility === 'restricted' ? 'internal' : article.visibility);
      setEditorTags(Array.isArray(article.tags) ? article.tags.join(', ') : '');
    } else {
      setEditorId(null);
      setEditorTitle('');
      setEditorCategoryId(categories[0]?.id || 1);
      setEditorSummary('');
      setEditorContent('');
      setEditorStatus('published');
      setEditorVisibility('public');
      setEditorTags('');
    }
    setViewMode('edit');
  };

  // Save article (Create or Update + RAG re-index)
  const handleSaveArticle = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editorTitle.trim() || !editorContent.trim()) {
      toast.error('Title and content are required');
      return;
    }

    setIsSaving(true);
    try {
      const tagsArray = editorTags
        .split(',')
        .map((t) => t.trim().toLowerCase())
        .filter(Boolean);

      const payload = {
        categoryId: editorCategoryId,
        title: editorTitle.trim(),
        summary: editorSummary.trim(),
        content: editorContent.trim(),
        status: editorStatus,
        visibility: editorVisibility,
        tags: tagsArray,
      };

      if (editorId) {
        await apiClient.put(`/support/kb/${editorId}`, payload);
        toast.success('Article updated and re-indexed in vector store');
      } else {
        await apiClient.post('/support/kb', payload);
        toast.success('Article created and embedded for AI retrieval');
      }

      await fetchArticles();
      setViewMode('browse');
    } catch {
      toast.error('Failed to save article');
    } finally {
      setIsSaving(false);
    }
  };

  // Delete article
  const handleDeleteArticle = async (id: number) => {
    if (!confirm('Are you sure you want to delete this knowledge base article?')) return;
    try {
      await apiClient.delete(`/support/kb/${id}`);
      toast.success('Article deleted');
      setViewMode('browse');
      fetchArticles();
    } catch {
      toast.error('Failed to delete article');
    }
  };

  // Load versions history
  const handleLoadVersions = async (id: number) => {
    try {
      const res = await apiClient.get(`/support/kb/${id}/versions`);
      setVersions(res.data?.data || []);
      setShowVersions(true);
    } catch {
      toast.error('Failed to load version history');
    }
  };

  // Feature Flag disabled state
  if (!kbFeatureEnabled) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 text-center bg-background text-foreground space-y-3">
        <div className="size-12 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-600 flex items-center justify-center">
          <BookOpen className="size-6" />
        </div>
        <div className="space-y-1">
          <div className="flex items-center justify-center gap-2">
            <h2 className="text-sm font-semibold text-foreground">Knowledge Base (Beta) is Disabled</h2>
            <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-primary/10 text-primary uppercase">Beta</span>
          </div>
          <p className="text-xs text-muted-foreground max-w-md">
            This module is currently gated behind an enterprise feature flag. To enable Knowledge Base and RAG embeddings, turn on the flag in Support Settings.
          </p>
        </div>
        <button
          onClick={() => navigate('/support/settings')}
          className="px-3.5 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 transition-colors shadow-2xs"
        >
          Open Support Settings
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-full space-y-4 overflow-x-hidden bg-background p-4 pb-16 font-sans text-xs text-foreground flex flex-col">
      {/* 1. Header with BETA Badge & Feedback Link */}
      <div className="p-4 rounded-xl border border-border bg-card flex flex-col sm:flex-row justify-between sm:items-center gap-3 shrink-0 shadow-xs">
        <div className="flex items-center gap-3">
          <div className="size-9 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 flex items-center justify-center font-bold">
            <BookOpen className="size-4.5" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-display font-semibold text-page-title tracking-tight text-foreground">
                Knowledge Base
              </h1>
              <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-300 dark:bg-emerald-950/60 dark:text-emerald-300 dark:border-emerald-800 uppercase tracking-wider">
                Beta
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              PostgreSQL-backed documentation · AI RAG search indexing · Deflection analytics
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Feedback link */}
          <a
            href="mailto:support-feedback@trustybytes.in?subject=Knowledge%20Base%20Beta%20Feedback"
            className="px-2.5 py-1.5 rounded-md border border-border bg-card hover:bg-secondary text-muted-foreground hover:text-foreground text-xs font-medium transition-colors flex items-center gap-1.5"
            title="Send beta feedback to the TeamOne engineering team"
          >
            <Send className="size-3 text-emerald-600" />
            <span>Beta Feedback</span>
          </a>

          {/* Mode Switcher */}
          <div className="flex items-center rounded-lg border border-border p-0.5 bg-secondary/40">
            <button
              onClick={() => { setActiveTab('articles'); setViewMode('browse'); }}
              className={cn(
                "px-3 py-1 text-xs rounded transition-colors cursor-pointer",
                activeTab === 'articles'
                  ? "bg-card text-foreground font-semibold shadow-2xs"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              Articles
            </button>
            <button
              onClick={() => { setActiveTab('analytics'); setViewMode('browse'); }}
              className={cn(
                "px-3 py-1 text-xs rounded transition-colors cursor-pointer flex items-center gap-1",
                activeTab === 'analytics'
                  ? "bg-card text-foreground font-semibold shadow-2xs"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              <BarChart3 className="size-3" />
              <span>Analytics</span>
            </button>
          </div>

          {/* New Article Action */}
          <button
            onClick={() => handleStartEdit()}
            className="px-3 py-1.5 rounded-lg bg-primary text-primary-foreground font-semibold text-xs hover:bg-primary/90 transition-colors flex items-center gap-1.5 shadow-2xs"
          >
            <Plus className="size-3.5" />
            <span>New Article</span>
          </button>
        </div>
      </div>

      {/* 2. Main Content Area */}
      {activeTab === 'analytics' ? (
        /* ═══════════════════════════════════════════════════════════════════ */
        /* ANALYTICS TAB (DB Backed queries)                                  */
        /* ═══════════════════════════════════════════════════════════════════ */
        <div className="flex-1 p-6 overflow-y-auto space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Deflection Metric */}
            <div className="p-4 rounded-xl border border-border bg-card shadow-2xs space-y-1.5">
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block">
                AI Chat Deflection Rate
              </span>
              <div className="flex items-baseline justify-between">
                <span className="text-2xl font-bold text-foreground">
                  {analytics?.aiDeflections.deflectionRatePct || 0}%
                </span>
                <span className="px-2 py-0.5 rounded text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-950/40">
                  {analytics?.aiDeflections.deflectedWithoutTicket || 0} resolved
                </span>
              </div>
              <p className="text-[11px] text-muted-foreground">
                Self-service inquiries answered via KB RAG without human escalation.
              </p>
            </div>

            {/* Total Articles Views */}
            <div className="p-4 rounded-xl border border-border bg-card shadow-2xs space-y-1.5">
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block">
                Top Article Engagement
              </span>
              <div className="flex items-baseline justify-between">
                <span className="text-2xl font-bold text-foreground">
                  {analytics?.topViewed.reduce((acc, curr) => acc + curr.views, 0) || 0}
                </span>
                <span className="text-[11px] font-medium text-muted-foreground">Total Views</span>
              </div>
              <p className="text-[11px] text-muted-foreground">
                Verified reader hits recorded in PostgreSQL.
              </p>
            </div>

            {/* Unhelpful Deflection Alerts */}
            <div className="p-4 rounded-xl border border-border bg-card shadow-2xs space-y-1.5">
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block">
                Review Required Articles
              </span>
              <div className="flex items-baseline justify-between">
                <span className="text-2xl font-bold text-rose-600">
                  {analytics?.unhelpful.length || 0}
                </span>
                <span className="text-[11px] font-medium text-rose-600 bg-rose-50 px-2 py-0.5 rounded dark:bg-rose-950/40">
                  Negative Rating
                </span>
              </div>
              <p className="text-[11px] text-muted-foreground">
                Articles with user feedback indicating outdated or unclear guidance.
              </p>
            </div>
          </div>

          {/* Tables */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top Viewed Articles */}
            <div className="rounded-xl border border-border bg-card p-4 space-y-3 shadow-2xs">
              <h3 className="font-semibold text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                <Eye className="size-3.5 text-primary" /> Most Viewed Knowledge Base Articles
              </h3>
              <div className="divide-y divide-border/60">
                {(analytics?.topViewed || []).length === 0 ? (
                  <p className="text-muted-foreground py-4 text-center">No views logged yet</p>
                ) : (
                  analytics?.topViewed.map((item) => (
                    <div key={item.id} className="py-2.5 flex items-center justify-between">
                      <span className="font-medium text-foreground truncate max-w-[280px]">
                        {item.title}
                      </span>
                      <div className="flex items-center gap-3 font-mono text-[11px] text-muted-foreground">
                        <span>{item.views} views</span>
                        <span className="text-emerald-600 font-semibold">{item.helpful} helpful</span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Unhelpful Articles */}
            <div className="rounded-xl border border-border bg-card p-4 space-y-3 shadow-2xs">
              <h3 className="font-semibold text-xs uppercase tracking-wider text-rose-600 flex items-center gap-1.5">
                <AlertTriangle className="size-3.5 text-rose-600" /> Articles Marked Unhelpful
              </h3>
              <div className="divide-y divide-border/60">
                {(analytics?.unhelpful || []).length === 0 ? (
                  <p className="text-muted-foreground py-4 text-center">No negative ratings logged</p>
                ) : (
                  analytics?.unhelpful.map((item) => (
                    <div key={item.id} className="py-2.5 flex items-center justify-between">
                      <span className="font-medium text-foreground truncate max-w-[280px]">
                        {item.title}
                      </span>
                      <span className="font-mono text-[11px] text-rose-600 font-bold">
                        {item.not_helpful} not helpful
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      ) : viewMode === 'edit' ? (
        /* ═══════════════════════════════════════════════════════════════════ */
        /* SPLIT VIEW ARTICLE EDITOR                                          */
        /* ═══════════════════════════════════════════════════════════════════ */
        <div className="flex-1 flex flex-col h-full overflow-hidden">
          {/* Editor Action Bar */}
          <div className="p-3 border-b border-border bg-secondary/30 flex items-center justify-between shrink-0">
            <button
              onClick={() => setViewMode(selectedArticle ? 'read' : 'browse')}
              className="px-2.5 py-1 rounded border border-border bg-card text-foreground text-xs flex items-center gap-1"
            >
              <ArrowLeft className="size-3.5" /> Back
            </button>

            <div className="flex items-center gap-2">
              <select
                value={editorStatus}
                onChange={(e) => setEditorStatus(e.target.value as any)}
                className="px-2.5 py-1 text-xs bg-card border border-border rounded text-foreground"
              >
                <option value="published">Published</option>
                <option value="draft">Draft</option>
                <option value="archived">Archived</option>
              </select>

              <select
                value={editorVisibility}
                onChange={(e) => setEditorVisibility(e.target.value as any)}
                className="px-2.5 py-1 text-xs bg-card border border-border rounded text-foreground"
              >
                <option value="public">Public (Portal & AI)</option>
                <option value="internal">Internal (Agents Only)</option>
              </select>

              <button
                onClick={handleSaveArticle}
                disabled={isSaving}
                className="px-4 py-1 rounded bg-primary text-primary-foreground font-semibold text-xs hover:bg-primary/90 transition-colors flex items-center gap-1"
              >
                {isSaving ? <RefreshCw className="size-3 animate-spin" /> : <Check className="size-3.5" />}
                <span>Save & Embed</span>
              </button>
            </div>
          </div>

          {/* Split Pane: Markdown Editor on Left, Live Preview on Right */}
          <div className="flex-1 flex overflow-hidden">
            {/* Left: Input Form */}
            <div className="flex-1 border-r border-border p-4 overflow-y-auto space-y-3 bg-card">
              <div>
                <label className="block text-[11px] font-semibold text-foreground mb-1 uppercase">Article Title *</label>
                <input
                  type="text"
                  value={editorTitle}
                  onChange={(e) => setEditorTitle(e.target.value)}
                  placeholder="e.g. Setting up VPN Multi-Factor Authentication"
                  className="w-full px-3 py-1.5 text-xs bg-secondary/30 border border-border rounded-lg text-foreground focus:outline-none focus:ring-1 focus:ring-primary font-medium"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-semibold text-foreground mb-1 uppercase">Category</label>
                  <select
                    value={editorCategoryId}
                    onChange={(e) => setEditorCategoryId(parseInt(e.target.value, 10))}
                    className="w-full px-3 py-1.5 text-xs bg-secondary/30 border border-border rounded-lg text-foreground"
                  >
                    {categories.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-[11px] font-semibold text-foreground mb-1 uppercase">Tags (comma-separated)</label>
                  <input
                    type="text"
                    value={editorTags}
                    onChange={(e) => setEditorTags(e.target.value)}
                    placeholder="vpn, security, network"
                    className="w-full px-3 py-1.5 text-xs bg-secondary/30 border border-border rounded-lg text-foreground font-mono"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-semibold text-foreground mb-1 uppercase">Short Executive Summary</label>
                <input
                  type="text"
                  value={editorSummary}
                  onChange={(e) => setEditorSummary(e.target.value)}
                  placeholder="Brief synopsis for search cards and AI deflection snippets..."
                  className="w-full px-3 py-1.5 text-xs bg-secondary/30 border border-border rounded-lg text-foreground"
                />
              </div>

              <div className="flex-1 flex flex-col">
                <label className="block text-[11px] font-semibold text-foreground mb-1 uppercase">
                  Markdown Content *
                </label>
                <textarea
                  rows={16}
                  value={editorContent}
                  onChange={(e) => setEditorContent(e.target.value)}
                  placeholder="## Prerequisites&#10;&#10;1. Step one...&#10;2. Step two..."
                  className="w-full px-3 py-2 text-xs font-mono bg-secondary/20 border border-border rounded-lg text-foreground focus:outline-none focus:ring-1 focus:ring-primary flex-1"
                />
              </div>
            </div>

            {/* Right: Live Render Preview */}
            <div className="flex-1 p-6 overflow-y-auto bg-background space-y-4">
              <span className="text-[10px] uppercase font-bold text-muted-foreground tracking-wider block">
                Live Markdown Preview
              </span>
              <div className="space-y-2 border-b border-border pb-3">
                <h2 className="text-base font-bold text-foreground">
                  {editorTitle || 'Untitled Article'}
                </h2>
                <p className="text-xs text-muted-foreground italic">
                  {editorSummary || 'No summary provided.'}
                </p>
              </div>

              <div className="prose prose-xs dark:prose-invert max-w-none whitespace-pre-wrap leading-relaxed text-foreground">
                {editorContent || 'Write markdown content on the left to see live rendering here.'}
              </div>
            </div>
          </div>
        </div>
      ) : viewMode === 'read' && selectedArticle ? (
        /* ═══════════════════════════════════════════════════════════════════ */
        /* ARTICLE READER VIEW                                                */
        /* ═══════════════════════════════════════════════════════════════════ */
        <div className="flex-1 flex flex-col h-full overflow-hidden">
          {/* Reader Top Bar */}
          <div className="p-3 border-b border-border bg-card flex items-center justify-between shrink-0 shadow-2xs">
            <button
              onClick={() => { setViewMode('browse'); setSelectedArticle(null); }}
              className="px-2.5 py-1 rounded border border-border bg-card hover:bg-secondary text-foreground text-xs flex items-center gap-1.5 transition-colors cursor-pointer"
            >
              <ArrowLeft className="size-3.5" /> Back to Articles
            </button>

            <div className="flex items-center gap-2">
              <button
                onClick={() => handleLoadVersions(selectedArticle.id)}
                className="px-2.5 py-1 rounded border border-border bg-card hover:bg-secondary text-foreground text-xs flex items-center gap-1.5"
              >
                <History className="size-3.5 text-muted-foreground" />
                <span>Versions</span>
              </button>

              <button
                onClick={() => handleStartEdit(selectedArticle)}
                className="px-2.5 py-1 rounded bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 flex items-center gap-1.5"
              >
                <Edit3 className="size-3.5" />
                <span>Edit Article</span>
              </button>

              <button
                onClick={() => handleDeleteArticle(selectedArticle.id)}
                className="p-1 rounded text-muted-foreground hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/40"
                title="Delete article"
              >
                <Trash2 className="size-3.5" />
              </button>
            </div>
          </div>

          {/* Reader Body */}
          <div className="flex-1 p-6 md:p-8 overflow-y-auto max-w-4xl mx-auto w-full space-y-6">
            {/* Meta Title */}
            <div className="space-y-2 border-b border-border pb-4">
              <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                <span className="font-semibold text-primary">{selectedArticle.category_name}</span>
                <span>•</span>
                <span>{selectedArticle.read_time_minutes} min read</span>
                <span>•</span>
                <span>Updated {new Date(selectedArticle.updated_at).toLocaleDateString()}</span>
              </div>
              <h1 className="text-xl font-bold tracking-tight text-foreground font-display">
                {selectedArticle.title}
              </h1>
              {selectedArticle.summary && (
                <p className="text-xs text-muted-foreground leading-relaxed">
                  {selectedArticle.summary}
                </p>
              )}
            </div>

            {/* Article Content */}
            <div className="prose prose-xs dark:prose-invert max-w-none whitespace-pre-wrap leading-relaxed text-foreground text-xs font-sans">
              {selectedArticle.content}
            </div>

            {/* Helpful Feedback Prompt */}
            <div className="p-4 rounded-xl border border-border bg-secondary/30 flex items-center justify-between mt-8">
              <div className="space-y-0.5">
                <span className="font-semibold text-xs text-foreground block">Was this article helpful?</span>
                <span className="text-[11px] text-muted-foreground font-mono">
                  {selectedArticle.helpful_count} people found this helpful ({selectedArticle.views_count} views)
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleFeedback(true)}
                  className="px-3 py-1.5 rounded-lg border border-border bg-card hover:bg-emerald-50 hover:text-emerald-700 hover:border-emerald-300 transition-colors flex items-center gap-1.5 text-xs font-semibold cursor-pointer shadow-2xs"
                >
                  <ThumbsUp className="size-3.5 text-emerald-600" />
                  <span>Yes, helpful</span>
                </button>
                <button
                  onClick={() => handleFeedback(false)}
                  className="px-3 py-1.5 rounded-lg border border-border bg-card hover:bg-rose-50 hover:text-rose-700 hover:border-rose-300 transition-colors flex items-center gap-1.5 text-xs font-semibold cursor-pointer shadow-2xs"
                >
                  <ThumbsDown className="size-3.5 text-rose-600" />
                  <span>No, improve</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* ═══════════════════════════════════════════════════════════════════ */
        /* BROWSE CATALOG: CATEGORY SIDEBAR + ARTICLES GRID                   */
        /* ═══════════════════════════════════════════════════════════════════ */
        <div className="flex flex-col lg:flex-row gap-5 items-start flex-1 min-h-0">
          {/* Category Sidebar - Styled like Settings left panel */}
          <aside className="w-full lg:w-64 shrink-0 rounded-xl border border-border bg-card p-3 shadow-xs space-y-4 lg:sticky lg:top-4">
            <div className="flex items-center gap-2.5 pb-3 border-b border-border">
              <div className="size-8 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
                <BookOpen className="size-4" />
              </div>
              <div>
                <h2 className="font-display font-semibold text-sm text-foreground">Categories</h2>
                <p className="text-[11px] text-muted-foreground">Knowledge repository</p>
              </div>
            </div>

            <nav className="space-y-4" aria-label="Knowledge Base Categories">
              <div className="space-y-1">
                <div className="px-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Categories
                </div>
                <div className="space-y-0.5">
                  <button
                    type="button"
                    onClick={() => setSearchParams((prev) => { const n = new URLSearchParams(prev); n.delete('category'); return n; })}
                    className={cn(
                      "w-full flex items-center justify-between gap-2 px-2.5 py-2 rounded-lg text-xs font-medium transition-colors text-left cursor-pointer",
                      !selectedCategoryId
                        ? "bg-primary text-primary-foreground font-semibold shadow-xs"
                        : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                    )}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <BookOpen className={cn("size-3.5 shrink-0", !selectedCategoryId ? "text-primary-foreground" : "text-muted-foreground")} />
                      <span className="truncate">All Articles</span>
                    </div>
                    <span className={cn(
                      "px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider",
                      !selectedCategoryId
                        ? "bg-primary-foreground/20 text-primary-foreground"
                        : "bg-muted text-muted-foreground"
                    )}>
                      {articles.length}
                    </span>
                  </button>

                  {categories.map((cat) => {
                    const isActive = selectedCategoryId === cat.id;
                    const CatIcon = getCategoryIcon(cat.name);
                    return (
                      <button
                        key={cat.id}
                        type="button"
                        onClick={() => setSearchParams((prev) => { const n = new URLSearchParams(prev); n.set('category', String(cat.id)); return n; })}
                        className={cn(
                          "w-full flex items-center justify-between gap-2 px-2.5 py-2 rounded-lg text-xs font-medium transition-colors text-left cursor-pointer",
                          isActive
                            ? "bg-primary text-primary-foreground font-semibold shadow-xs"
                            : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                        )}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <CatIcon className={cn("size-3.5 shrink-0", isActive ? "text-primary-foreground" : "text-muted-foreground")} />
                          <span className="truncate">{cat.name}</span>
                        </div>
                        <span className={cn(
                          "px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider",
                          isActive
                            ? "bg-primary-foreground/20 text-primary-foreground"
                            : "bg-muted text-muted-foreground"
                        )}>
                          {cat.article_count || 0}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </nav>
          </aside>

          {/* Articles Feed Workspace */}
          <main className="flex-1 min-w-0 space-y-4 w-full">
            {/* Search Bar & Status Filters */}
            <div className="flex flex-col sm:flex-row items-center gap-3 p-3 bg-card border border-border rounded-xl shadow-xs">
              <div className="relative flex-1 w-full">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchParams((prev) => { const n = new URLSearchParams(prev); if (e.target.value) n.set('q', e.target.value); else n.delete('q'); return n; })}
                  placeholder="Search articles by title, content, or tag..."
                  className="w-full pl-9 pr-3 py-1.5 text-xs bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary shadow-2xs"
                />
              </div>

              {/* Status Selector */}
              <div className="flex items-center rounded-lg border border-border p-0.5 bg-secondary/40 shrink-0">
                {['published', 'draft', 'archived', 'all'].map((st) => (
                  <button
                    key={st}
                    onClick={() => setSearchParams((prev) => { const n = new URLSearchParams(prev); n.set('status', st); return n; })}
                    className={cn(
                      "px-2.5 py-1 text-xs rounded capitalize transition-colors cursor-pointer",
                      statusFilter === st
                        ? "bg-card text-foreground font-semibold shadow-2xs"
                        : "text-muted-foreground hover:text-foreground"
                    )}
                  >
                    {st}
                  </button>
                ))}
              </div>
            </div>

            {/* Articles List / Cards */}
            {isLoading ? (
              <div className="py-16 text-center text-muted-foreground bg-card rounded-xl border border-border shadow-xs">
                <RefreshCw className="size-5 animate-spin mx-auto mb-2 text-primary" />
                <p>Loading published articles...</p>
              </div>
            ) : articles.length === 0 ? (
              <div className="py-16 text-center text-muted-foreground space-y-2 bg-card rounded-xl border border-border shadow-xs">
                <BookOpen className="size-10 mx-auto text-muted-foreground/30 stroke-1" />
                <p className="font-semibold text-foreground text-xs">No Knowledge Base articles found</p>
                <p className="text-[11px] max-w-sm mx-auto">
                  No articles matched your criteria. Create a new article to populate the team repository.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3.5">
                {articles.map((art) => (
                  <div
                    key={art.id}
                    onClick={() => handleOpenArticle(art)}
                    className="p-4 rounded-xl border border-border bg-card hover:border-primary/50 hover:shadow-xs transition-all cursor-pointer flex flex-col justify-between space-y-3 group"
                  >
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                        <span className="font-semibold text-primary truncate max-w-[150px]">
                          {art.category_name}
                        </span>
                        <span className="font-mono">{art.read_time_minutes || 2} min read</span>
                      </div>

                      <h3 className="font-semibold text-xs text-foreground group-hover:text-primary transition-colors line-clamp-2">
                        {art.title}
                      </h3>

                      <p className="text-muted-foreground text-[11px] line-clamp-2 leading-relaxed">
                        {art.summary || art.content.slice(0, 100)}
                      </p>
                    </div>

                    <div className="pt-2 border-t border-border/60 flex items-center justify-between text-[10px] text-muted-foreground font-mono">
                      <span>{art.views_count || 0} views</span>
                      <span className="text-emerald-600 font-bold">{art.helpful_count || 0} helpful</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </main>
        </div>
      )}

      {/* Version History Modal */}
      {showVersions && selectedArticle && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-2xs p-4">
          <div className="bg-card border border-border rounded-xl shadow-2xl max-w-lg w-full p-5 space-y-4">
            <div className="flex items-center justify-between border-b border-border pb-2">
              <h3 className="font-semibold text-xs text-foreground uppercase tracking-wider flex items-center gap-1.5">
                <History className="size-4 text-primary" />
                <span>Article Versions History</span>
              </h3>
              <button onClick={() => setShowVersions(false)}>
                <ArrowLeft className="size-4 text-muted-foreground" />
              </button>
            </div>

            <div className="divide-y divide-border/60 max-h-72 overflow-y-auto">
              {versions.length === 0 ? (
                <p className="text-muted-foreground py-4 text-center">No previous revisions</p>
              ) : (
                versions.map((ver) => (
                  <div key={ver.id} className="py-2.5 flex items-center justify-between text-xs">
                    <div>
                      <span className="font-bold text-foreground block">Version {ver.version_number}</span>
                      <span className="text-[10px] text-muted-foreground">{new Date(ver.created_at).toLocaleString()}</span>
                    </div>
                    <button
                      onClick={async () => {
                        try {
                          await apiClient.post(`/support/kb/${selectedArticle.id}/versions/${ver.version_number}/restore`);
                          toast.success(`Restored to Version ${ver.version_number}`);
                          setShowVersions(false);
                          fetchArticles();
                        } catch {
                          toast.error('Failed to restore version');
                        }
                      }}
                      className="px-2.5 py-1 rounded border border-border text-xs hover:bg-secondary font-medium"
                    >
                      Restore
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default KnowledgeBase;
